import { getItemUniqueKey, getPackageId } from './cart'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import moment from 'moment'
import { extractPkgInclusions } from 'lib/offer/inclusionUtils'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import getLEPackageItemTotals from './getLEPackageItemTotals'
import getAccommodationItemViewLoyaltyUpgradeAvailability from '../loyalty/getAccommodationItemViewLoyaltyUpgradeAvailability'
import { isPostPurchaseAncillaryCartMode } from 'checkout/selectors/payment/isPostPurchaseAncillaryPayment'
import { PAYMENT_SCHEDULE_STATUS } from 'constants/payment'
import { isLpcHotelOffer } from 'lib/offer/offerTypes'
import type { IntlShape } from 'react-intl'

export function dummyItemTotalsView(): App.Checkout.ItemViewTotals {
  return {
    price: 0,
    memberPrice: 0,
    value: 0,
    surcharge: 0,
    taxesAndFees: 0,
    extraGuestSurcharge: 0,
    memberValue: 0,
  }
}

function dummyAccommodationItemView(
  intl: IntlShape,
  item: App.Checkout.LEAccommodationItem,
  kind: 'le',
  offer?: App.Offer,
): App.Checkout.LEAccommodationItemView {
  const dummy: App.Checkout.LEAccommodationItemView = {
    ...dummyItemTotalsView(),
    kind,
    packageId: getPackageId(item),
    uniqueKey: getItemUniqueKey(item),
    roomTypeId: '',
    item,
    primaryTitle: '',
    mainSummaryLabel: '',
    image: { id: '' },
    occupancy: { adults: 0, children: 0, infants: 0, teenagers: 0 },
    removeLabel: intl.formatMessage({
      id: '6GCTUDzlaHS2YZWTQ1KS',
      defaultMessage: 'Remove',
      description: 'Remove button label for accommodation item in checkout',
    }),
    partnership: { bonusPoints: 0, bonusDescription: '', localRewardConversionRate: 0 },
    totals: {
      extraGuestSurcharge: 0,
      price: 0,
      memberPrice: 0,
      taxesAndFees: 0,
      value: 0,
      memberValue: 0,
      surcharge: 0,
    },
    amenities: [],
    policies: '',
    hasTactical: false,
    inclusions: [],
    bonusInclusions: [],
    loyalty: {
      productType: getLoyaltyProductType(offer),
    },
    title: '',
  }

  return dummy
}

export function getLEPackageItemView(
  intl: IntlShape,
  item: App.Checkout.LEAccommodationItem,
  calendarsByOccupancy: App.CalendarState['calendarsByOccupancy'],
  availableRatesByOccupancy: { [offerId: string]: App.OfferAvailableRatesByOccupancy },
  checkoutWithMemberPrice: boolean,
  offer?: App.Offer,
  postPurchase?: App.CheckoutCartMode,
  existingOrder?: App.Order,
  luxPlusTier?: App.MembershipSubscriptionTier,
  canApplyLoyaltyRoomUpgrade?: boolean,
  adjustedPaymentSchedule?: App.AdjustedPaymentScheduleChargeData,
): App.WithDataStatus<App.Checkout.LEAccommodationItemView> {
  if (!offer) {
    return {
      hasRequiredData: false,
      data: dummyAccommodationItemView(intl, item, 'le'),
    }
  }

  const uniqueKey = getItemUniqueKey(item)
  const originPackage = offer.packages.find(pkg => pkg.uniqueKey === uniqueKey)
  const loyaltyUpgradeAvailability = canApplyLoyaltyRoomUpgrade ? getAccommodationItemViewLoyaltyUpgradeAvailability(
    offer,
    item,
    calendarsByOccupancy,
    availableRatesByOccupancy,
  ) : undefined

  let itemTotals: App.Checkout.ItemViewTotals | undefined
  if (offer && originPackage) {
    if (isInstantBookingLEHotelItem(item) && !isPostPurchaseAncillaryCartMode(postPurchase)) {
      itemTotals = getLEPackageItemTotals(
        offer,
        originPackage,
        item.checkIn,
        item.checkOut,
        item.occupancy,
        undefined,
        calendarsByOccupancy,
        availableRatesByOccupancy,
      )
    } else {
      itemTotals = {
        price: originPackage.price,
        memberPrice: originPackage.memberPrice,
        value: originPackage.value,
        taxesAndFees: originPackage.taxesAndFees,
        otherFees: {
          propertyFees: originPackage.propertyFees,
        },
        memberValue: 0,
        surcharge: 0,
        extraGuestSurcharge: 0,
      }
    }
  } else if (existingOrder && isPostPurchaseAncillaryCartMode(postPurchase)) {
    const existingItem = existingOrder.items.find(orderItem => orderItem.transactionKey === item.transactionKey)
    if (existingItem) {
      itemTotals = {
        price: existingItem.packagePrice,
        memberPrice: existingItem.memberPrice,
        value: existingItem.package.value,
        taxesAndFees: 0,
        otherFees: {
          propertyFees: existingItem?.propertyFees,
        },
        memberValue: 0,
        surcharge: 0,
        extraGuestSurcharge: 0,
      }
    }
  }

  const hasAppliedLoyaltyUpgrade = !!(canApplyLoyaltyRoomUpgrade && item.loyaltyAppliedUpgradeType)

  if (
    !itemTotals ||
    (hasAppliedLoyaltyUpgrade && !loyaltyUpgradeAvailability)
  ) {
    return {
      hasRequiredData: false,
      data: dummyAccommodationItemView(intl, item, 'le', offer),
    }
  }

  const targetPackageUniqueKey = item.loyaltyAppliedUpgradeType ? originPackage?.loyalty.upgradeTargetsUniqueKeys?.[item.loyaltyAppliedUpgradeType] : undefined
  const pkg = targetPackageUniqueKey ? offer.packages.find(pkg => pkg.uniqueKey === targetPackageUniqueKey) : originPackage

  // If the item has a new price, use that instead
  if (item.newPrice) {
    itemTotals.price = item.newPrice
  }
  if (item.newSurcharge) {
    itemTotals.surcharge = item.newSurcharge + (itemTotals.extraGuestSurcharge ?? 0)
  }
  if (item.newExtraGuestSurcharge) {
    itemTotals.extraGuestSurcharge = item.newExtraGuestSurcharge
  }

  if (
    (postPurchase === 'change-package' ||
      postPurchase === 'select-date' ||
      postPurchase === 'add-extra-guests' ||
      postPurchase === 'change-dates'
    ) && 'orderItemId' in item && existingOrder) {
    const orderItem = existingOrder.items.find(orderItem => orderItem.id === item.orderItemId)
    if (orderItem) {
      itemTotals.paidPrice = orderItem.total

      if (existingOrder.paymentScheduleDetails && existingOrder.paymentScheduleDetails?.status === PAYMENT_SCHEDULE_STATUS.ACTIVE) {
        itemTotals.paymentScheduleAdjustment = adjustedPaymentSchedule?.summary.payNowAmount ?? 0
      }

      if (offer.type === 'hotel' && postPurchase === 'select-date') {
        // For BNBL select-date: preserve original package price, no reduction in surcharges if new package price is lower.
        const priceToUse = orderItem.packagePrice
        const memberPriceToUse = orderItem.packagePrice

        const nextPrice = checkoutWithMemberPrice && memberPriceToUse > 0 ? memberPriceToUse : priceToUse
        itemTotals.newPrice = nextPrice + ((itemTotals.isAvailable ? itemTotals.surcharge : orderItem.reservation?.surcharge) || 0)

        // Calculate difference, but do not allow negative amounts
        const priceDifference = itemTotals.newPrice - orderItem.total
        itemTotals.price = Math.max(0, priceDifference)
        itemTotals.memberPrice = Math.max(0, priceDifference)
      } else if (
        (offer.type === 'hotel' && ['change-dates', 'add-extra-guests'].includes(postPurchase)) ||
        (isLpcHotelOffer(offer) && postPurchase === 'change-dates')
      ) {
        // If we are changing the package at the same time as changing the dates
        const newPackageSelected = orderItem.package.duration !== item.duration || (pkg && orderItem.package.uniqueKey !== pkg.uniqueKey)

        let priceToUse = newPackageSelected ? itemTotals.price : orderItem.packagePrice
        if (orderItem.channelMarkupId || isLpcHotelOffer(offer)) {
          priceToUse = itemTotals.price
        }
        const memberPriceToUse = (newPackageSelected || isLpcHotelOffer(offer)) ? itemTotals.memberPrice : orderItem.packagePrice

        const nextPrice = checkoutWithMemberPrice && memberPriceToUse > 0 ? memberPriceToUse : priceToUse
        itemTotals.newPrice = nextPrice + ((itemTotals.isAvailable ? itemTotals.surcharge : orderItem.reservation?.surcharge) || 0)
        itemTotals.price = itemTotals.newPrice - orderItem.total
        itemTotals.memberPrice = itemTotals.newPrice - orderItem.total
      } else if (isLpcHotelOffer(offer) && postPurchase === 'add-extra-guests') {
        const nextPrice = checkoutWithMemberPrice && itemTotals.memberPrice > 0 ? itemTotals.memberPrice : itemTotals.price
        itemTotals.newPrice = nextPrice + (itemTotals.surcharge || 0)
        itemTotals.price = Math.max(0, itemTotals.newPrice - orderItem.total)
        itemTotals.memberPrice = Math.max(0, itemTotals.newPrice - orderItem.total)
      } else if (item.isUpdated) {
        const nextPrice = checkoutWithMemberPrice && itemTotals.memberPrice > 0 ? itemTotals.memberPrice : itemTotals.price
        itemTotals.newPrice = nextPrice + (itemTotals.surcharge || 0)
        itemTotals.price = itemTotals.newPrice - orderItem.total
        itemTotals.memberPrice = itemTotals.newPrice - orderItem.total
      } else {
        itemTotals.memberPrice = 0
        itemTotals.newPrice = 0
        itemTotals.price = 0
      }
      // clear those prices that we don't want to display
      itemTotals.value = 0
      itemTotals.surcharge = 0
      itemTotals.taxesAndFees = 0
      itemTotals.extraGuestSurcharge = 0
      itemTotals.otherFees!.propertyFees = 0
    }
  }

  const checkIn = 'checkIn' in item ? moment(item.checkIn) : undefined
  const checkOut = 'checkOut' in item ? moment(item.checkOut) : undefined

  const [inclusions, bonusInclusions, luxPlusInclusions] = extractPkgInclusions(pkg, { checkIn, checkOut, luxPlusTier })

  const data: App.Checkout.LEAccommodationItemView = {
    title: `${pkg?.roomType?.name}: ${offer.property?.name}`,
    totals: {
      ...itemTotals,
      isReservableForZero: pkg?.roomRate?.isReservableForZero,
    },
    kind: 'le',
    pkg,
    packageId: item.packageId,
    uniqueKey,
    roomTypeId: pkg?.roomType?.id ?? '',
    item,
    primaryTitle: offer.type === 'hotel' ? pkg?.name ?? '' : pkg?.roomType?.name ?? '',
    mainSummaryLabel: pkg?.name ?? '',
    image: pkg?.roomType?.images[0] ?? offer.image,
    amenities: pkg?.roomType?.amenities ?? [],
    policies: pkg?.roomPolicyDescription ?? '',
    occupancy: item.occupancy,
    inclusions,
    bonusInclusions,
    luxPlusInclusions,
    removeLabel: item.itemType === 'tourV1'
      ? intl.formatMessage({
          id: 'kMOB9efOHiyP5PebUXOJ',
          defaultMessage: 'Remove tour',
          description: 'Remove button label for tour item in checkout in the checkout flow',
        })
      : intl.formatMessage(
          {
            id: '8q3c6d0Yp3bswGUzbmU1',
            defaultMessage: 'Remove {saleUnit}',
            description: 'Remove button label for accommodation item in checkout with sale unit type',
          },
          {
            saleUnit: offer?.saleUnitLong ?? intl.formatMessage({
              id: 'ExBPXgHuuX0D9_IQhNZG',
              defaultMessage: 'room',
              description: 'Default sale unit name for accommodation remove button',
            }),
          },
        ),
    partnership: pkg?.partnership,
    hasTactical: pkg?.hasTactical ?? false,
    loyalty: {
      productType: getLoyaltyProductType(offer),
    },
    mx: getHotelEncryptedMargin(offer?.type, pkg),
    offer,
    loyaltyUpgradeAvailability,
  }

  const hasRequiredData = !!itemTotals.price || !!postPurchase

  return {
    hasRequiredData,
    data,
  }
}
